import EventBus from "./eventBus";

export default class StreamManager {
  constructor({ bus = new EventBus() } = {}) {
    this.bus = bus;
    this.devices = [];
    this.screenStream = null;
    this.microphoneStream = null;
    this.cameraStreams = new Map();
    this.deviceListener = null;
  }

  async init() {
    await this.refreshDevices();
    if (navigator.mediaDevices?.addEventListener) {
      this.deviceListener = () => this.refreshDevices();
      navigator.mediaDevices.addEventListener(
        "devicechange",
        this.deviceListener
      );
    }
  }

  async refreshDevices() {
    try {
      this.devices = await navigator.mediaDevices.enumerateDevices();
      this.bus.emit("devices:updated", this.devices);
      return this.devices;
    } catch (error) {
      console.error("Failed to enumerate devices", error);
      this.bus.emit("devices:error", error);
      throw error;
    }
  }

  getVideoInputs() {
    return this.devices.filter((device) => device.kind === "videoinput");
  }

  getAudioInputs() {
    return this.devices.filter((device) => device.kind === "audioinput");
  }

  async getScreenStream(constraints = {}) {
    const defaultConstraints = {
      video: {
        frameRate: { ideal: 30 },
        width: { ideal: 1920 },
        height: { ideal: 1080 },
        ...constraints.video,
      },
      audio: constraints.audio ?? false,
    };

    try {
      this.screenStream = await navigator.mediaDevices.getDisplayMedia(
        defaultConstraints
      );
      this.bus.emit("stream:screen", this.screenStream);
      return this.screenStream;
    } catch (error) {
      if (defaultConstraints.audio) {
        console.warn(
          "getDisplayMedia with audio failed; retrying with audio: false",
          error
        );
        try {
          this.screenStream = await navigator.mediaDevices.getDisplayMedia({
            video: defaultConstraints.video,
            audio: false,
          });
          this.bus.emit("stream:screen", this.screenStream);
          return this.screenStream;
        } catch (retryError) {
          this.bus.emit("stream:error", { type: "screen", error: retryError });
          throw retryError;
        }
      }
      this.bus.emit("stream:error", { type: "screen", error });
      throw error;
    }
  }

  async getMicrophoneStream({ deviceId, constraints = {} } = {}) {
    try {
      const defaultConstraints = {
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100,
          ...constraints.audio,
        },
        video: false,
      };

      if (deviceId) {
        defaultConstraints.audio.deviceId = { exact: deviceId };
      } else if (defaultConstraints.audio?.deviceId) {
        if (typeof defaultConstraints.audio.deviceId === "string") {
          defaultConstraints.audio.deviceId = {
            exact: defaultConstraints.audio.deviceId,
          };
        }
      }
      this.microphoneStream = await navigator.mediaDevices.getUserMedia(
        defaultConstraints
      );
      this.bus.emit("stream:microphone", this.microphoneStream);
      return this.microphoneStream;
    } catch (error) {
      this.bus.emit("stream:error", { type: "microphone", error });
      throw error;
    }
  }

  async getCameraStream(deviceId, constraints = {}) {
    try {
      if (this.cameraStreams.has(deviceId)) {
        return this.cameraStreams.get(deviceId);
      }

      const videoConstraints = {
        width: { ideal: 640, max: 1280 },
        height: { ideal: 360, max: 720 },
        frameRate: { ideal: 24, max: 30 },
        ...constraints.video,
      };

      if (deviceId) {
        videoConstraints.deviceId = { exact: deviceId };
      } else if (typeof videoConstraints.deviceId === "string") {
        videoConstraints.deviceId = { exact: videoConstraints.deviceId };
      }

      const defaultConstraints = {
        audio: false,
        video: videoConstraints,
      };

      const stream = await navigator.mediaDevices.getUserMedia(
        defaultConstraints
      );
      this.cameraStreams.set(deviceId, stream);
      this.bus.emit("stream:camera", { deviceId, stream });
      return stream;
    } catch (error) {
      this.bus.emit("stream:error", { type: "camera", deviceId, error });
      throw error;
    }
  }

  async getCameraStreams(deviceIds = [], constraints = {}) {
    const streams = await Promise.all(
      deviceIds.map((id) => this.getCameraStream(id, constraints))
    );
    return streams;
  }

  releaseCamera(deviceId) {
    if (!this.cameraStreams.has(deviceId)) return;
    this.stopStream(this.cameraStreams.get(deviceId));
    this.cameraStreams.delete(deviceId);
  }

  stopStream(stream) {
    if (!stream) return;
    stream.getTracks().forEach((track) => track.stop());
  }

  stopAll() {
    this.stopStream(this.screenStream);
    this.stopStream(this.microphoneStream);
    [...this.cameraStreams.entries()].forEach(([deviceId, stream]) => {
      this.stopStream(stream);
      this.cameraStreams.delete(deviceId);
    });
    this.screenStream = null;
    this.microphoneStream = null;
  }

  destroy() {
    this.stopAll();
    if (navigator.mediaDevices?.removeEventListener && this.deviceListener) {
      navigator.mediaDevices.removeEventListener(
        "devicechange",
        this.deviceListener
      );
    }
  }
}
